'use client'

import type {
  NotificationActionEnum,
  NotificationContextProps,
  NotificationToastType,
  NotificationTypeEnum,
  ShowToastParams,
} from '@maestro/commerce-blocks/components/notification/notification.types'
import type { EventPayloadType, Price } from '@maestro/data-layer'

import { useTranslations } from 'next-intl'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

import AddedToCartNotification from '@maestro/commerce-blocks/components/cart/added-to-cart-notification'
import {} from '@maestro/commerce-blocks/components/notification/batons/use-notification-toast'
import { useNotificationToastFocus } from '@maestro/commerce-blocks/hooks/use-notification-toast-focus'
import { formatPriceAsText } from '@maestro/commerce-blocks/utils/price.utils'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { EventType } from '@maestro/data-layer/models/event'
import { theme } from '@maestro/local/theme'

export const NotificationContext = createContext<NotificationContextProps>({
  toasts: [],
  setToasts: () => null,
  showSuccess: () => null,
  showInfo: () => null,
  showError: () => null,
  removeToast: () => null,
  showUpdatedCart: () => null,
  showInteractiveToast: () => null,
  removeLastToast: () => null,
})

export const useNotificationContext = () => useContext(NotificationContext)

const PERSISTENT_TOAST_TYPES = new Set(['error', 'added-to-cart-notification'])

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [toasts, setToasts] = useState([] as NotificationToastType[])
  const [removalTimeouts, setRemovalTimeouts] = useState<
    Record<number, NodeJS.Timeout>
  >({})
  const autoRemoveTimersRef = useRef<
    Record<number, ReturnType<typeof setTimeout>>
  >({})
  const t = useTranslations('commerce-blocks')
  const { addedToCartTimeout, autoClose, timeout } = theme().notification
  const { publish } = useEventBusContext()
  const { clearToastUserInteraction, isToastUserInteracted } =
    useNotificationToastFocus()

  const removeToast = useCallback(
    (id: number) => {
      if (autoRemoveTimersRef.current[id]) {
        clearTimeout(autoRemoveTimersRef.current[id])
        delete autoRemoveTimersRef.current[id]
      }
      clearToastUserInteraction(id)

      setToasts((prevToasts) =>
        prevToasts.map((toast) =>
          toast.id === id ? { ...toast, removed: true } : toast
        )
      )

      const timeout = setTimeout(() => {
        setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id))
        setRemovalTimeouts((prevTimeouts) => {
          const newTimeouts = { ...prevTimeouts }
          delete newTimeouts[id]
          return newTimeouts
        })
      }, 250)

      setRemovalTimeouts((prevTimeouts) => ({ ...prevTimeouts, [id]: timeout }))
    },
    [clearToastUserInteraction]
  )

  const showToast = useCallback(
    ({
      title,
      description,
      announceMessage,
      type,
      showClose,
      content,
      id,
      autoClose: toastAutoClose = autoClose,
      timeout: customTimeout,
      eventData,
    }: ShowToastParams) => {
      if (id && toasts.find((toast) => toast.id === id)) {
        if (removalTimeouts[id]) {
          clearTimeout(removalTimeouts[id])
          setRemovalTimeouts((prevTimeouts) => {
            const newTimeouts = { ...prevTimeouts }
            delete newTimeouts[id]
            return newTimeouts
          })
        }
        return
      }

      const toast = {
        id: id || Date.now() + Math.floor(Math.random() * 1000000),
        title,
        description,
        announceMessage,
        type,
        showClose,
        content,
        timeout: customTimeout,
      } satisfies NotificationToastType

      setToasts((prevToasts) => [...prevToasts, toast])

      const alertType =
        eventData?.alertType ??
        (type === 'added-to-cart-notification' || type === 'interactive'
          ? 'success'
          : type)

      publish(EventType.ALERT_SHOWN, {
        alertType,
        alertTitle:
          eventData?.alertTitle ?? (typeof title === 'string' ? title : ''),
      })

      const shouldAutoCloseToast =
        (autoClose || !showClose) &&
        toastAutoClose &&
        !PERSISTENT_TOAST_TYPES.has(type)

      if (shouldAutoCloseToast) {
        const duration = customTimeout ?? timeout
        const toastId = toast.id

        const tryRemove = () => {
          if (!autoRemoveTimersRef.current[toastId]) {
            return
          }
          if (isToastUserInteracted(toastId)) {
            autoRemoveTimersRef.current[toastId] = setTimeout(tryRemove, 500)
          } else {
            delete autoRemoveTimersRef.current[toastId]
            removeToast(toastId)
          }
        }

        autoRemoveTimersRef.current[toastId] = setTimeout(tryRemove, duration)
      }
    },
    [
      autoClose,
      toasts,
      publish,
      removalTimeouts,
      timeout,
      isToastUserInteracted,
      removeToast,
    ]
  )

  const showSuccess = useCallback(
    (title: string, description?: string, timeout?: number) => {
      showToast({
        title,
        description,
        type: 'success',
        showClose: true,
        timeout,
      })
    },
    [showToast]
  )

  const showInfo = useCallback(
    (title: string, description?: string, timeout?: number) => {
      showToast({ title, description, type: 'info', showClose: true, timeout })
    },
    [showToast]
  )

  const showError = useCallback(
    (title: string, description?: string, timeout?: number) => {
      showToast({ title, description, type: 'error', showClose: true, timeout })
    },
    [showToast]
  )

  const showUpdatedCart = useCallback(
    (
      type: NotificationTypeEnum,
      action: NotificationActionEnum = 'added',
      totalPrice: Price,
      productCount: number
    ) => {
      const actionMessage =
        action === 'removed'
          ? t('notification.removed-from-cart')
          : t('notification.added-to-cart')
      const cartStateMessage = t('cart.contains-items', {
        count: productCount,
      })
      const totalPriceMessage = totalPrice
        ? `${t('cart.total')}: ${formatPriceAsText(totalPrice)}`
        : ''
      const announceMessage = [
        actionMessage,
        cartStateMessage,
        totalPriceMessage,
      ]
        .filter(Boolean)
        .join('. ')

      switch (type) {
        case 'regular':
          showToast({
            title: actionMessage,
            description: '',
            announceMessage,
            type: 'added-to-cart-notification',
            showClose: true,
            content: (
              <AddedToCartNotification
                productCount={productCount}
                totalPrice={totalPrice}
              />
            ),
            id: -1,
            timeout: action === 'removed' ? timeout : addedToCartTimeout,
          })
          break

        case 'minimal':
          showToast({
            title: actionMessage,
            description: '',
            announceMessage,
            type: 'success',
            showClose: true,
            timeout: action === 'removed' ? timeout : addedToCartTimeout,
          })
          break
      }
    },
    [addedToCartTimeout, showToast, t, timeout]
  )

  /**
   * Interactive toasts require user action to be dismissed.
   * Might contain React nodes as title/description.
   */
  const showInteractiveToast = useCallback(
    (
      title: string | React.ReactNode,
      description?: string | React.ReactNode,
      eventData?: EventPayloadType[EventType.ALERT_SHOWN],
      timeout?: number
    ) => {
      showToast({
        title,
        description,
        type: 'interactive',
        showClose: true,
        autoClose: false,
        timeout,
        eventData,
      })
    },
    [showToast]
  )

  const removeLastToast = useCallback(() => {
    removeToast(-1)
  }, [removeToast])

  const value = useMemo(() => {
    return {
      removeLastToast,
      removeToast,
      setToasts,
      showError,
      showInfo,
      showSuccess,
      showUpdatedCart,
      showInteractiveToast,
      toasts,
    }
  }, [
    removeLastToast,
    removeToast,
    showError,
    showInfo,
    showSuccess,
    showUpdatedCart,
    showInteractiveToast,
    toasts,
  ])

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}
